// pages/Transformations.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import {
  Search,
  FilterList,
  Sort,
  TrendingUp,
  Bolt,
  Schedule,
  AttachMoney,
  Group,
  Psychology,
  AutoGraph,
  Timeline,
  CheckCircle,
  Warning,
  Info,
  ArrowForward,
  Star,
  StarBorder,
  Share,
  Download
} from '@mui/icons-material';
import { useIEP } from '../contexts/IEPContext';
import TransformationCard from '../components/IEP/TransformationCard';
import TransformationDetail from '../components/IEP/TransformationDetail';

const Transformations = () => {
  const { transformations, organization, iepEngine } = useIEP();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterComplexity, setFilterComplexity] = useState('all');
  const [sortBy, setSortBy] = useState('recommended');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTransformation, setSelectedTransformation] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [filteredTransformations, setFilteredTransformations] = useState([]);
  
  const itemsPerPage = 9;
  
  const categories = [
    'all',
    'strategic',
    'operational',
    'cultural',
    'technical',
    'innovation',
    'financial',
    'ecosystem',
    'transcendent',
    'leadership',
    'organizational',
    'risk',
    'ethical',
    'governance',
    'sustainability',
    'learning',
    'adaptive',
    'biological',
    'quantum',
    'consciousness'
  ];
  
  const priorities = ['all', 'critical', 'high', 'medium', 'low'];
  const complexities = ['all', 'high', 'medium', 'low'];
  const sortOptions = [
    { value: 'recommended', label: 'AI Recommended' },
    { value: 'impact', label: 'Impact Score' },
    { value: 'priority', label: 'Priority' },
    { value: 'duration', label: 'Duration' },
    { value: 'roi', label: 'ROI' },
    { value: 'alphabetical', label: 'Alphabetical' }
  ];

  useEffect(() => {
    if (transformations) {
      let filtered = Object.values(transformations);
      
      // Apply search filter
      if (searchTerm) {
        filtered = filtered.filter(t => 
          t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          t.category.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      // Apply category filter
      if (filterCategory !== 'all') {
        filtered = filtered.filter(t => t.category === filterCategory);
      }
      
      // Apply priority filter
      if (filterPriority !== 'all') {
        filtered = filtered.filter(t => t.priority === filterPriority);
      }
      
      // Apply complexity filter
      if (filterComplexity !== 'all') {
        filtered = filtered.filter(t => t.complexity === filterComplexity);
      }
      
      // Apply sorting
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'recommended':
            // AI recommendation logic
            const aScore = iepEngine?.calculateTransformationImpactScore(a, [], organization) || 0;
            const bScore = iepEngine?.calculateTransformationImpactScore(b, [], organization) || 0;
            return bScore - aScore;
          case 'impact':
            return b.impactScore - a.impactScore;
          case 'priority':
            const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
            return priorityOrder[a.priority] - priorityOrder[b.priority];
          case 'duration':
            return a.expectedDuration.min - b.expectedDuration.min;
          case 'roi':
            const aRoi = parseFloat(iepEngine?.estimateROI(a, organization) || 0);
            const bRoi = parseFloat(iepEngine?.estimateROI(b, organization) || 0);
            return bRoi - aRoi;
          case 'alphabetical':
            return a.name.localeCompare(b.name);
          default:
            return 0;
        }
      });
      
      setFilteredTransformations(filtered);
    }
  }, [transformations, searchTerm, filterCategory, filterPriority, filterComplexity, sortBy, organization, iepEngine]);

  const handleTransformationClick = (transformation) => {
    setSelectedTransformation(transformation);
    setDetailOpen(true);
  };

  const handleFavoriteToggle = (id) => {
    setFavorites(prev => 
      prev.includes(id) 
        ? prev.filter(favId => favId !== id)
        : [...prev, id]
    );
  };

  const handleAddToRoadmap = (transformation) => {
    // Implementation for adding to roadmap
    console.log('Adding to roadmap:', transformation);
  };

  const totalPages = Math.ceil(filteredTransformations.length / itemsPerPage);
  const paginatedTransformations = filteredTransformations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <Box>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h1" gutterBottom>
          Transformations Library
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Browse 96 AI-powered transformations to evolve your organization
        </Typography>
      </Box>

      {/* Stats Bar */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="primary.main">
              96
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Transformations
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              24
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Critical Priority
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="warning.main">
              12
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Quick Wins ({'<'}30 days)  
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h4" color="secondary.main">
              14336
            </Typography>
            <Typography variant="body2" color="text-secondary">
              Q* Dimensions
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search transformations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filterCategory}
                  label="Category"
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  {categories.map(category => (
                    <MenuItem key={category} value={category}>
                      {category.charAt(0).toUpperCase() + category.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filterPriority}
                  label="Priority"
                  onChange={(e) => setFilterPriority(e.target.value)}
                >
                  {priorities.map(priority => (
                    <MenuItem key={priority} value={priority}>
                      {priority.charAt(0).toUpperCase() + priority.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Complexity</InputLabel>
                <Select
                  value={filterComplexity}
                  label="Complexity"
                  onChange={(e) => setFilterComplexity(e.target.value)}
                >
                  {complexities.map(complexity => (
                    <MenuItem key={complexity} value={complexity}>
                      {complexity.charAt(0).toUpperCase() + complexity.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  {sortOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {/* Active Filters */}
          <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
            {filterCategory !== 'all' && (
              <Chip 
                label={`Category: ${filterCategory}`} 
                onDelete={() => setFilterCategory('all')}
                size="small"
              />
            )}
            {filterPriority !== 'all' && (
              <Chip 
                label={`Priority: ${filterPriority}`} 
                onDelete={() => setFilterPriority('all')}
                size="small"
              />
            )}
            {filterComplexity !== 'all' && (
              <Chip 
                label={`Complexity: ${filterComplexity}`} 
                onDelete={() => setFilterComplexity('all')}
                size="small"
              />
            )}
            {searchTerm && (
              <Chip 
                label={`Search: ${searchTerm}`} 
                onDelete={() => setSearchTerm('')}
                size="small"
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Transformations Grid */}
      <Grid container spacing={3}>
        {paginatedTransformations.map((transformation) => (
          <Grid item xs={12} sm={6} md={4} key={transformation.id}>
            <Card 
              elevation={2}
              sx={{ 
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 8
                }
              }}
              onClick={() => handleTransformationClick(transformation)}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {transformation.name}
                    </Typography>
                    <Chip 
                      label={transformation.category}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <IconButton 
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFavoriteToggle(transformation.id);
                    }}
                  >
                    {favorites.includes(transformation.id) ? (
                      <Star color="warning" />
                    ) : (
                      <StarBorder />
                    )}
                  </IconButton>
                </Box>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {transformation.description?.substring(0, 100)}...
                </Typography>
                
                <Box mb={2}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Bolt fontSize="small" sx={{ mr: 1, color: 'warning.main' }} />
                    <Typography variant="body2">
                      Priority: <strong>{transformation.priority}</strong>
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Schedule fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
                    <Typography variant="body2">
                      Duration: <strong>{transformation.expectedDuration?.min}-{transformation.expectedDuration?.max} days</strong>
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center">
                    <AttachMoney fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="body2">
                      Budget: <strong>${(transformation.resourceRequirements?.budget?.min / 1000).toFixed(0)}K-${(transformation.resourceRequirements?.budget?.max / 1000).toFixed(0)}K</strong>
                    </Typography>
                  </Box>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToRoadmap(transformation);
                    }}
                  >
                    Add to Roadmap
                  </Button>
                  <Box display="flex" gap={1}>
                    <Chip 
                      label={transformation.complexity}
                      size="small"
                      color={transformation.complexity === 'high' ? 'error' : transformation.complexity === 'medium' ? 'warning' : 'success'}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination 
            count={totalPages} 
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* No Results */}
      {filteredTransformations.length === 0 && (
        <Box textAlign="center" py={8}>
          <Psychology sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No transformations found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or search terms
          </Typography>
        </Box>
      )}

      {/* Transformation Detail Dialog */}
      <Dialog 
        open={detailOpen} 
        onClose={() => setDetailOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(145deg, #1e293b, #0f172a)',
            border: '1px solid #334155'
          }
        }}
      >
        {selectedTransformation && (
          <TransformationDetail 
            transformation={selectedTransformation}
            onClose={() => setDetailOpen(false)}
            onAddToRoadmap={handleAddToRoadmap}
          />
        )}
      </Dialog>
    </Box>
  );
};

export default Transformations;